import dayjs from "dayjs";
import { BASE_URL } from "@/constants/endpoints";
import {
  ApiResponse,
  GetLoadedTaxesBackendQuery,
  LoadedTaxesListResponse,
  TaxType,
  TaxesUploadResult,
  TaxUploadHistoryItem,
} from "@/pages/config/taxes/schema/taxes.schema";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import type { AxiosInstance } from "axios";

const isRecordErrorsMessage = (message: string): boolean => {
  const m = (message ?? "").toLowerCase();
  return m.includes("fila(s) con errores") || m.includes("errores en los registros");
};

let taxesApiPromise: Promise<AxiosInstance> | null = null;

const BASE_PATH = "/Impuestos";

const getTaxesApi = async (): Promise<AxiosInstance | null> => {
  if (taxesApiPromise) return taxesApiPromise;
  taxesApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return taxesApiPromise;
};

export const getTaxesUploadHistory = async (): Promise<
  TaxUploadHistoryItem[]
> => {
  const api = await getTaxesApi();
  if (!api) return [];

  const response = await api.get(`${BASE_PATH}/historial-carga`);

  return response.data;
};

export const getTaxTypes = async (): Promise<TaxType[]> => {
  const api = await getTaxesApi();
  if (!api) return [];

  const response = await api.get(`${BASE_PATH}/tipos-impuesto`);

  return response.data;
};

export interface UploadTaxesFileProps {
  file: File;
}

export const uploadTaxesFile = async ({
  file,
}: UploadTaxesFileProps): Promise<{
  item: TaxUploadHistoryItem;
  result: TaxesUploadResult;
}> => {
  const api = await getTaxesApi();
  if (!api) {
    return {
      item: {
        id: String(Date.now()),
        fechaCarga: dayjs().format("DD/MM/YYYY"),
        nombreArchivo: file.name,
        estado: "Error",
        detalle: "-",
      },
      result: {
        status: "invalid",
        message: "No se pudo inicializar el servicio",
      },
    };
  }

  const formData = new FormData();
  formData.append("file", file);

  let message = "";
  let uploadSucceeded = true;
  try {
    const response = await api.post(`${BASE_PATH}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const data = response.data;
    const payload =
      data && typeof data === "object" && "success" in data
        ? (data as ApiResponse<string>)
        : { success: true, message: String(data?.message ?? data ?? "") };

    message = String(payload?.message ?? "");
    uploadSucceeded = Boolean((payload as any)?.success ?? true);
  } catch (error: any) {
    uploadSucceeded = false;
    const backendMessage =
      String(error?.response?.data?.message ?? error?.message ?? "") ||
      "No se pudo procesar el archivo";
    message = backendMessage;
  }

  const history = await getTaxesUploadHistory();
  const mostRecentForFile =
    history.find((h) => h.nombreArchivo === file.name) ?? history[0];

  if (isRecordErrorsMessage(message) || mostRecentForFile?.errores?.length) {
    return {
      item: mostRecentForFile,
      result: {
        status: "recordErrors",
        message: message || "Existen errores en los registros importados",
        errores: mostRecentForFile?.errores,
        detalle: mostRecentForFile?.detalle,
      },
    };
  }

  if (uploadSucceeded) {
    return {
      item:
        mostRecentForFile ??
        ({
          id: String(Date.now()),
          fechaCarga: dayjs().format("DD/MM/YYYY"),
          nombreArchivo: file.name,
          estado: "Completado",
          detalle: "-",
        }),
      result: {
        status: "success",
        message: message || "Archivo procesado correctamente",
        detalle: mostRecentForFile?.detalle,
      },
    };
  }

  return {
    item:
      mostRecentForFile ??
      ({
        id: String(Date.now()),
        fechaCarga: dayjs().format("DD/MM/YYYY"),
        nombreArchivo: file.name,
        estado: "Error",
        detalle: "-",
      }),
    result: {
      status: "invalid",
      message: message || "No se pudo procesar el archivo",
      detalle: mostRecentForFile?.detalle,
    },
  };
};

export const getLoadedTaxes = async (
  props?: Partial<GetLoadedTaxesBackendQuery>,
): Promise<LoadedTaxesListResponse | null> => {
  const api = await getTaxesApi();
  if (!api) return null;

  const qs = props ? objectToQueryStringWithArray(props) : "";
  const url = qs ? `${BASE_PATH}/cargados?${qs}` : `${BASE_PATH}/cargados`;

  const response = await api.get(url);

  return response.data;
};

export const resendLoadedTax = async (id: number): Promise<boolean> => {
  const api = await getTaxesApi();
  if (!api) return false;

  const response = await api.put(`${BASE_PATH}/${id}/reenviar`);
  const data = response.data;
  const payload =
    data && typeof data === "object" && "success" in data
      ? (data as ApiResponse<string>)
      : { success: true };
  return Boolean(payload?.success);
};
